import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { StatusPill } from '../../components/StatusPills';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, CheckCircle2, Save } from 'lucide-react';
import { toast } from 'sonner';
import AliasManager from './AliasManager';
import { verificationLabel, missingFieldLabels } from '../../lib/canonicalOrg';

const TYPE_OPTIONS = [
  'council', 'fostering_agency', 'childrens_home', 'adoption',
  'school', 'doctor', 'gp', 'hospital', 'police', 'mental_health',
];

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-rule last:border-0 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-foreground tabular-nums">{value ?? '—'}</span>
  </div>
);

const AdminOrgDetail = () => {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getOrg(orgId);
      setOrg(res.data);
      setForm({
        name: res.data.name || '',
        type: res.data.type || '',
        privacy_email: res.data.privacy_email || '',
        correspondence_email: res.data.correspondence_email || '',
        address: res.data.address || '',
        website: res.data.website || '',
        admin_notes: res.data.admin_notes || '',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const emailChanged = useMemo(
    () => !!form && (form.privacy_email || '').trim().toLowerCase() !== (org?.privacy_email || '').toLowerCase(),
    [form, org],
  );

  if (loading || !org || !form) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateOrg(orgId, {
        name: form.name,
        type: form.type,
        privacy_email: form.privacy_email,
        correspondence_email: form.correspondence_email,
        address: form.address,
        website: form.website,
        admin_notes: form.admin_notes,
      });
      toast.success('Saved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    try {
      await adminApi.verifyOrg(orgId);
      toast.success('Verified');
      load();
    } catch {
      toast.error('Failed to verify');
    }
  };

  const stats = org.stats || {};

  return (
    <div className="space-y-5" data-testid="admin-org-detail">
      <Link to="/admin/organisations" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> All organisations
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-2xl font-semibold text-foreground">{org.name}</h1>
            <StatusPill tone={org.admin_verified ? 'done' : 'needs'} size="sm">{verificationLabel(org)}</StatusPill>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {org.linked_user_count} linked user{org.linked_user_count === 1 ? '' : 's'}
          </p>
          {org.verified_via === 'reply' && (
            <p className="text-sm text-muted-foreground mt-1" data-testid="admin-org-verified-by-reply">
              Verified automatically when the organisation acknowledged a request from its own email domain.
              Other users are offered it now.
            </p>
          )}
          {!org.admin_verified && (
            <p className="text-sm text-muted-foreground mt-1">
              Only the person who added it is offered this until it's verified.
            </p>
          )}
          {missingFieldLabels(org).length > 0 && (
            <p className="text-sm text-muted-foreground mt-1" data-testid="admin-org-missing">
              Missing {missingFieldLabels(org).join(', ')}.
            </p>
          )}
        </div>
        {(!org.admin_verified || org.verified_via === 'reply') && (
          <Button onClick={verify} data-testid="admin-org-verify-btn">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> {org.admin_verified ? 'Mark checked' : 'Mark verified'}
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="admin-org-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="admin-org-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Privacy email *</Label>
              <Input
                type="email"
                value={form.privacy_email}
                onChange={(e) => setForm({ ...form, privacy_email: e.target.value })}
                                data-testid="admin-org-privacy-email-input"
              />
              {emailChanged && form.privacy_email && (
                <div className="text-xs text-amber-ink bg-amber-wash rounded-lg px-3 py-2">
                  <span>
                    Changing the privacy email will set the new value as the unique key. The old email
                    ({org.privacy_email || 'none'}) will be recorded in admin notes for traceability.
                  </span>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Correspondence email</Label>
                <Input
                  type="email"
                  value={form.correspondence_email}
                  onChange={(e) => setForm({ ...form, correspondence_email: e.target.value })}
                                  />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Website</Label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                                  />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Address</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Aliases</Label>
              <AliasManager
                orgId={orgId}
                aliases={org.aliases || []}
                onChange={(next) => setOrg({ ...org, aliases: next })}
              />
              <p className="text-xs text-muted-foreground">
                Aliases are searched alongside the canonical name in autocomplete.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Admin notes</Label>
              <Textarea
                value={form.admin_notes}
                onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                rows={4}
                placeholder="Internal notes. Not shown to users."
              />
            </div>

            <div className="flex justify-end pt-1">
              <Button onClick={save} disabled={saving} data-testid="admin-org-save-btn">
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats panel */}
        <Card>
          <CardContent className="p-5 space-y-1">
            <p className="font-heading text-base font-semibold text-foreground mb-2">SAR statistics</p>
            <StatRow label="Total SARs" value={stats.total_sars} />
            <StatRow label="Distinct users (internal)" value={stats.distinct_users} />
            <StatRow label="Avg response (days)" value={stats.avg_response_days} />
            <StatRow label="Avg emails per SAR" value={stats.avg_emails_per_sar} />
            <StatRow label="Extension rate" value={stats.extension_pct != null ? `${stats.extension_pct}%` : null} />
            <StatRow label="ID-request rate" value={stats.id_request_pct != null ? `${stats.id_request_pct}%` : null} />
            <StatRow label="Portal-link rate" value={stats.portal_pct != null ? `${stats.portal_pct}%` : null} />
            <StatRow label="Refusal rate" value={stats.refusal_pct != null ? `${stats.refusal_pct}%` : null} />
            <StatRow label="Redaction rate" value={stats.redaction_pct != null ? `${stats.redaction_pct}%` : null} />

            <p className="font-heading text-sm font-semibold text-foreground pt-3 pb-1">Care patterns</p>
            <StatRow label="Distinct social workers" value={stats.distinct_social_workers} />
            <StatRow label="Avg social workers / user" value={stats.avg_social_workers_per_user} />
            <StatRow label="Total placements" value={stats.total_placements} />
            <StatRow label="Avg placements / user" value={stats.avg_placements_per_user} />
            <StatRow label="Avg placement duration (months)" value={stats.avg_placement_duration_months} />

            {Object.keys(stats.placement_type_pct || {}).length > 0 && (
              <>
                <p className="font-heading text-sm font-semibold text-foreground pt-3 pb-1">Placement type mix</p>
                {Object.entries(stats.placement_type_pct).map(([k, v]) => (
                  <StatRow key={k} label={k} value={`${v}%`} />
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOrgDetail;
